import { db } from '@/db/client';
import { getUserIdFromSupabaseUser } from '@/lib/auth-utils';
import { getSupabase } from '@/lib/supabase';
import type { SessionEvent, SessionId } from '@/schemas';
import { encodeEventToRow } from './codec';
import { getSyncState, setLastUploadedAt } from './state';

export interface UploadStats {
  uploaded: number;
}

export async function uploadPendingEvents(sessionId: SessionId): Promise<UploadStats> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync events');
  }

  const userId = getUserIdFromSupabaseUser(session.user);

  const { lastUploadedAt = 0 } = await getSyncState(sessionId);
  const pending: SessionEvent[] = await db.sessionEvents
    .where('sessionId')
    .equals(sessionId)
    .and((e) => e.timestamp > lastUploadedAt)
    .sortBy('timestamp');

  if (pending.length === 0) return { uploaded: 0 };

  // Ensure all events have userId set (for events created before authentication)
  const rows = pending.map((e) => ({
    ...encodeEventToRow(e),
    user_id: e.userId ?? userId,
  }));
  const { error } = await client.from('session_events').upsert(rows, { onConflict: 'id' });
  if (error) throw error;

  const maxTs = pending.at(-1)?.timestamp ?? lastUploadedAt;
  await setLastUploadedAt(sessionId, maxTs);
  return { uploaded: pending.length };
}
